import React, { useState, useEffect } from 'react';
import './FanimeLandingPage.css';
import LoginModal from './LoginModal';
import UsernameCreation from './UsernameCreation';
import PermissionsScreen from './PermissionsScreen';
import AvatarSelection from './AvatarSelection';
import ProfileMenu from './ProfileMenu';
import InfoModal from './InfoModal';
import { roomAPI, authAPI } from '../utils/apiUtils';
import avatarAssets from '../utils/avatarAssets';
import { APP_NAME, APP_DESCRIPTION } from '../constants/appConstants';
import { UI_CONFIG } from '../constants/config';
// Analytics imports
import { trackEvent } from '../firebase/analytics';
import { ANALYTICS_EVENTS } from '../firebase/analyticsEvents';

// Modal content data
const modalContent = {
  about: (
    <div>
      <h3>Welcome to {APP_NAME}</h3>
      <p>
        {APP_NAME} is {APP_DESCRIPTION}
        Our mission is to create meaningful connections in a fun, safe, and creative environment.
      </p>
      
      <h4>What Makes Us Special</h4>
      <ul>
        <li><strong>Avatar-Based Interaction:</strong> Express yourself through customizable 3D avatars and Live2D characters</li>
        <li><strong>Spontaneous Connections:</strong> Meet new people from around the world instantly</li>
        <li><strong>Safe Environment:</strong> Advanced moderation and reporting systems keep our community friendly</li>
        <li><strong>Creative Expression:</strong> Multiple avatar types, backgrounds, and customization options</li>
      </ul>
      
      <h4>Our Vision</h4>
      <p>
        We believe that everyone deserves to express themselves authentically while building genuine friendships. 
        {APP_NAME} combines cutting-edge technology with human connection to create something truly special.
      </p>
      
      <h4>Join Our Community</h4>
      <p>
        Whether you're looking to practice languages, make new friends, or just have fun conversations, 
        {APP_NAME} provides the perfect platform for meaningful social interaction.
      </p>
    </div>
  ),
  
  tos: (
    <div>
      <h3>Terms of Service</h3>
      <p><em>Last updated: July 2025</em></p>
      
      <h4>1. Acceptance of Terms</h4>
      <p>
        By accessing and using {APP_NAME}, you accept and agree to be bound by the terms and provision of this agreement.
      </p>
      
      <h4>2. Use License</h4>
      <p>
        Permission is granted to temporarily access {APP_NAME} for personal, non-commercial transitory viewing only. 
        This is the grant of a license, not a transfer of title.
      </p>
      
      <h4>3. User Conduct</h4>
      <p>You agree not to:</p>
      <ul>
        <li>Use the service for any unlawful purpose or to solicit others to unlawful acts</li>
        <li>Violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
        <li>Infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
        <li>Harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
        <li>Submit false or misleading information</li>
      </ul>
      
      <h4>4. Account Termination</h4>
      <p>
        We may terminate or suspend your account immediately, without prior notice, if you breach the Terms of Service.
      </p>
      
      <h4>5. Privacy</h4>
      <p>
        Your privacy is important to us. Please review our Privacy Policy to understand how we collect and use your information.
      </p>
      
      <h4>6. Changes to Terms</h4>
      <p>
        We reserve the right to modify these terms at any time. Users will be notified of any changes.
      </p>
    </div>
  ),
  
  privacy: (
    <div>
      <h3>Privacy Policy</h3>
      <p><em>Last updated: July 2025</em></p>
      
      <h4>Information We Collect</h4>
      <ul>
        <li><strong>Account Information:</strong> Email, username, and profile information</li>
        <li><strong>Usage Data:</strong> How you interact with our service, session duration, features used</li>
        <li><strong>Communication Data:</strong> Chat messages and voice data during sessions (not stored permanently)</li>
        <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
      </ul>
      
      <h4>How We Use Your Information</h4>
      <ul>
        <li>Provide and maintain our service</li>
        <li>Improve user experience and features</li>
        <li>Ensure platform safety and security</li>
        <li>Send important updates and notifications</li>
      </ul>
      
      <h4>Data Protection</h4>
      <p>
        We implement industry-standard security measures to protect your data. 
        Your information is encrypted and stored securely.
      </p>
      
      <h4>Third-Party Services</h4>
      <p>
        We use Google authentication for secure login. Google's privacy policy applies to their services.
      </p>
      
      <h4>Contact Us</h4>
      <p>
        For privacy-related questions, please contact us at privacy@wybli.com
      </p>
    </div>
  ),
  
  guidelines: (
    <div>
      <h3>Community Guidelines</h3>
      <p>
        {APP_NAME} is built on respect, kindness, and genuine connection. These guidelines help ensure 
        everyone has a positive experience.
      </p>
      
      <h4>🤝 Be Respectful</h4>
      <ul>
        <li>Treat everyone with kindness and respect</li>
        <li>Respect others' boundaries and comfort levels</li>
        <li>Use appropriate language and behavior</li>
        <li>Respect cultural and personal differences</li>
      </ul>
      
      <h4>🚫 Prohibited Content</h4>
      <ul>
        <li>Nudity, sexual content, or suggestive behavior</li>
        <li>Harassment, bullying, or hate speech</li>
        <li>Spam, scams, or promotional content</li>
        <li>Sharing personal information (yours or others')</li>
        <li>Illegal activities or dangerous behavior</li>
      </ul>
      
      <h4>🛡️ Safety First</h4>
      <ul>
        <li>Never share personal information like full name, address, or phone number</li>
        <li>Report inappropriate behavior immediately</li>
        <li>Use the skip feature if you feel uncomfortable</li>
        <li>Trust your instincts - if something feels wrong, it probably is</li>
      </ul>
      
      <h4>🎭 Avatar Guidelines</h4>
      <ul>
        <li>Keep avatars appropriate and non-offensive</li>
        <li>No impersonation of real people or fictional characters for deception</li>
        <li>Avoid avatars that could be disturbing to others</li>
      </ul>
      
      <h4>📢 Reporting</h4>
      <p>
        If you encounter behavior that violates these guidelines, please report it immediately. 
        Our moderation team reviews all reports promptly.
      </p>
      
      <h4>⚠️ Consequences</h4>
      <p>
        Violations may result in warnings, temporary suspensions, or permanent bans, 
        depending on the severity and frequency of the violation.
      </p>
    </div>
  ),
  
  faq: (
    <div>
      <h3>Frequently Asked Questions</h3>
      
      <h4>Getting Started</h4>
      <p><strong>Q: How do I start chatting?</strong></p>
      <p>A: Simply log in with your Google account, select an avatar, and click "Start Chatting" to be matched with someone!</p>
      
      <p><strong>Q: Do I need to download anything?</strong></p>
      <p>A: No! {APP_NAME} works entirely in your web browser. Just make sure you have a modern browser with camera and microphone access.</p>
      
      <h4>Technical Questions</h4>
      <p><strong>Q: What browsers are supported?</strong></p>
      <p>A: We support Chrome, Firefox, Safari, and Edge. Chrome typically provides the best experience.</p>
      
      <p><strong>Q: Why can't others hear me?</strong></p>
      <p>A: Check that you've granted microphone permissions and that your microphone isn't muted. Look for the microphone icon in your browser's address bar.</p>
      
      <p><strong>Q: The video isn't working. What should I do?</strong></p>
      <p>A: Ensure you've granted camera permissions and that no other applications are using your camera.</p>
      
      <h4>Features</h4>
      <p><strong>Q: Can I change my avatar during a chat?</strong></p>
      <p>A: Currently, avatars are set before entering a chat session. You can change your avatar for future chats from your profile.</p>
      
      <p><strong>Q: How long do chat sessions last?</strong></p>
      <p>A: Each session has a timer that you can see on screen. You can also skip to the next person at any time.</p>
      
      <p><strong>Q: Are conversations recorded?</strong></p>
      <p>A: No, we do not record or store your video or audio conversations. Your privacy is our priority.</p>
      
      <h4>Safety & Moderation</h4>
      <p><strong>Q: How do I report inappropriate behavior?</strong></p>
      <p>A: Click the report button during or immediately after a conversation. Our moderation team reviews all reports.</p>
      
      <p><strong>Q: Can I block specific users?</strong></p>
      <p>A: Yes, you can block users to prevent future matches with them.</p>
      
      <h4>Account & Profile</h4>
      <p><strong>Q: How do I delete my account?</strong></p>
      <p>A: Contact our support team, and they'll help you delete your account and associated data.</p>
      
      <p><strong>Q: Can I use {APP_NAME} without logging in?</strong></p>
      <p>A: Currently, we require Google account authentication to ensure safety and prevent abuse.</p>
      
      <h4>Getting Help</h4>
      <p>Contact our support team at support@wybli.com or join our Discord community for help!</p>
    </div>
  )
};

const FanimeLandingPage = ({ onStartChatting }) => {
  const [user, setUser] = useState(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isUsernameCreationOpen, setIsUsernameCreationOpen] = useState(false);
  const [isPermissionsScreenOpen, setIsPermissionsScreenOpen] = useState(false);
  const [isAvatarSelectionOpen, setIsAvatarSelectionOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isTosModalOpen, setIsTosModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isGuidelinesModalOpen, setIsGuidelinesModalOpen] = useState(false);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [isMatchmaking, setIsMatchmaking] = useState(false);
  const [loginContext, setLoginContext] = useState('general');
  const [pendingUserInfo, setPendingUserInfo] = useState(null);
  const [loadedGifs, setLoadedGifs] = useState(new Set());
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const [showSafariWarning, setShowSafariWarning] = useState(false);

  // Check if device is mobile
  const isMobileDevice = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    return mobileRegex.test(userAgent) || window.innerWidth <= 768;
  };

  // Check if browser is Safari on PC
  const isSafariPC = () => {
    const userAgent = navigator.userAgent;
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    return isSafari && !isMobile;
  };

  // Check for mobile device and Safari browser on component mount
  useEffect(() => {
    if (isMobileDevice() && UI_CONFIG.SHOW_MOBILE_WARNING) {
      setShowMobileWarning(true);
    } else if (isSafariPC()) {
      setShowSafariWarning(true);
    }
  }, []);

  // Monitor user state changes for debugging
  useEffect(() => {
    console.log('=== USER STATE CHANGED ===');
    console.log('Current user:', user);
    console.log('User selectedAvatar:', user?.selectedAvatar);
    console.log('User avatarId:', user?.avatarId);
    console.log('==========================');
  }, [user]);

  // Handler functions
  const handleLogin = (userInfo) => {
    console.log('=== LOGIN DEBUG ===');
    console.log('User logged in:', userInfo);
    console.log('Username from userInfo:', userInfo.username);
    console.log('Auth provider:', userInfo.authProvider);
    console.log('API Response:', userInfo.apiResponse);
    
    // IMPORTANT: Save the backend token immediately after login for API calls
    if (userInfo.backendToken) {
      try {
        localStorage.setItem('backend_jwt_token', userInfo.backendToken);
        localStorage.setItem('auth_token', userInfo.backendToken);
        console.log('Backend JWT Token stored immediately for API calls');
      } catch (error) {
        console.error('Failed to save backend token:', error);
        alert('Warning: Failed to save authentication token. Some features may not work properly.');
      }
    } else {
      console.warn('No backend token received from login response');
    }
    
    if (userInfo.googleToken) {
      try {
        localStorage.setItem('google_jwt_token', userInfo.googleToken);
        console.log('Google JWT Token stored immediately');
      } catch (error) {
        console.error('Failed to save Google token:', error);
      }
    }
    
    // Check if user has completed username setup before
    const hasCompletedUsernameSetup = localStorage.getItem('username_setup_completed');
    console.log('Has completed username setup:', hasCompletedUsernameSetup);
    console.log('==================');
    
    // Show username creation screen if:
    // 1. User has never completed username setup before, OR
    // 2. No username in response, OR  
    // 3. Backend explicitly says this is a new user
    const shouldCreateUsername = 
      !hasCompletedUsernameSetup ||
      !userInfo.username || 
      userInfo.username === '' || 
      userInfo.username === null || 
      userInfo.username === undefined ||
      (userInfo.apiResponse && userInfo.apiResponse.isNewUser === true) ||
      (userInfo.apiResponse && userInfo.apiResponse.user && userInfo.apiResponse.user.isNewUser === true);
    
    if (shouldCreateUsername) {
      // Show username creation screen
      console.log('Showing username creation screen');
      setPendingUserInfo(userInfo);
      setIsUsernameCreationOpen(true);
    } else {
      // Complete login
      console.log('Completing login with existing username');
      setUser(userInfo);
      
      // Store user info in localStorage
      localStorage.setItem('user', JSON.stringify(userInfo));
      
      // Store tokens separately for easy access (already done above, but keeping for completeness)
      if (userInfo.googleToken) {
        localStorage.setItem('google_jwt_token', userInfo.googleToken);
        console.log('Google JWT Token stored in localStorage as "google_jwt_token"');
      }
      
      if (userInfo.backendToken) {
        localStorage.setItem('backend_jwt_token', userInfo.backendToken);
        console.log('Backend JWT Token stored in localStorage as "backend_jwt_token"');
      }
      
      // Store the backend token as the primary auth token
      if (userInfo.backendToken) {
        localStorage.setItem('auth_token', userInfo.backendToken);
        console.log('Backend token set as primary auth token');
      }
    }
    
    // Track login event
    trackEvent(ANALYTICS_EVENTS.USER_LOGIN, {
      method: 'google',
      timestamp: Date.now()
    });
  };

  const handleUsernameCreationComplete = (updatedUserInfo) => {
    // If this is being called from the change username option, update the user immediately
    if (user && user.id === updatedUserInfo.id) {
      console.log('Username changed, updating current user');
      setUser(updatedUserInfo);
      localStorage.setItem('user', JSON.stringify(updatedUserInfo));
      setIsUsernameCreationOpen(false);
      setPendingUserInfo(null);
    } else {
      // Check if this is first time setup and permissions haven't been granted
      const cameraPermission = localStorage.getItem('camera_permission');
      const micPermission = localStorage.getItem('mic_permission');
      
      console.log('=== PERMISSIONS CHECK ===');
      console.log('Camera permission from localStorage:', cameraPermission);
      console.log('Mic permission from localStorage:', micPermission);
      console.log('User exists:', !!user);
      console.log('User ID:', user?.id);
      console.log('Updated user info ID:', updatedUserInfo?.id);
      console.log('========================');
      
      // TEMPORARY: Force permissions screen for testing
      console.log('FORCING PERMISSIONS SCREEN FOR TESTING');
      setPendingUserInfo(updatedUserInfo);
      setIsUsernameCreationOpen(false);
      setIsPermissionsScreenOpen(true);
      return;
      
      if (!cameraPermission || !micPermission) {
        // First time setup - show permissions screen
        console.log('First time setup - showing permissions screen');
        setPendingUserInfo(updatedUserInfo);
        setIsUsernameCreationOpen(false);
        setIsPermissionsScreenOpen(true);
      } else {
        // Returning user - go directly to avatar selection
        console.log('Returning user - going to avatar selection');
        setPendingUserInfo(updatedUserInfo);
        setIsUsernameCreationOpen(false);
        setIsAvatarSelectionOpen(true);
      }
    }
    
    // Track username creation
    trackEvent(ANALYTICS_EVENTS.USERNAME_CREATED, {
      timestamp: Date.now()
    });
  };

  const handlePermissionsComplete = (updatedUserInfo) => {
    console.log('=== PERMISSIONS COMPLETE ===');
    console.log('Updated user info:', updatedUserInfo);
    console.log('Camera permission:', updatedUserInfo.cameraPermission);
    console.log('Mic permission:', updatedUserInfo.micPermission);
    console.log('================================');
    
    // Move to avatar selection
    console.log('Permissions complete, moving to avatar selection');
    setPendingUserInfo(updatedUserInfo);
    setIsPermissionsScreenOpen(false);
    setIsAvatarSelectionOpen(true);
    
    // Track permissions completion
    trackEvent(ANALYTICS_EVENTS.PERMISSIONS_GRANTED, {
      camera_permission: updatedUserInfo.cameraPermission,
      mic_permission: updatedUserInfo.micPermission,
      timestamp: Date.now()
    });
  };

  const handleAvatarSelectionComplete = (finalUserInfo) => {
    console.log('=== AVATAR SELECTION COMPLETE ===');
    console.log('Final user info:', finalUserInfo);
    console.log('Selected avatar:', finalUserInfo.selectedAvatar);
    console.log('Avatar ID:', finalUserInfo.avatarId);
    console.log('Username:', finalUserInfo.username);
    console.log('Login context:', loginContext);
    console.log('================================');
    
    // Store final user info in localStorage first
    localStorage.setItem('user', JSON.stringify(finalUserInfo));
    
    // Store tokens separately for easy access
    if (finalUserInfo.googleToken) {
      localStorage.setItem('google_jwt_token', finalUserInfo.googleToken);
      console.log('Google JWT Token stored in localStorage as "google_jwt_token"');
    }
    
    if (finalUserInfo.backendToken) {
      localStorage.setItem('backend_jwt_token', finalUserInfo.backendToken);
      console.log('Backend JWT Token stored in localStorage as "backend_jwt_token"');
    }
    
    // Store the backend token as the primary auth token
    if (finalUserInfo.backendToken) {
      localStorage.setItem('auth_token', finalUserInfo.backendToken);
      console.log('Backend token set as primary auth token');
    }
    
    // If this is during onboarding (not changing avatar), mark setup as completed
    if (!user || !user.username) {
      localStorage.setItem('username_setup_completed', 'true');
      localStorage.setItem('avatar_setup_completed', 'true');
    }
    
    setIsAvatarSelectionOpen(false);
    setPendingUserInfo(null);
    
    // Track avatar selection
    trackEvent(ANALYTICS_EVENTS.AVATAR_SELECTED, {
      avatar_id: finalUserInfo.avatarId,
      timestamp: Date.now()
    });
    
    // If login was triggered from Start Chat, proceed to chat
    if (loginContext === 'chat') {
      console.log('Login context is "chat", proceeding to chat...');
      // Reset login context to prevent future issues
      setLoginContext('general');
      
      // Set user state and then proceed to chat
      setUser(finalUserInfo);
      console.log('User state updated:', finalUserInfo);
      
      // Use setTimeout to ensure state is updated before calling handleStartChat
      setTimeout(() => {
        console.log('Calling handleStartChat after user state update...');
        handleStartChat(true); // Pass true to indicate it was from avatar selection
      }, 100);
    } else {
      console.log('Login context is not "chat", staying on landing page. Context:', loginContext);
      // Set user state for normal flow
      setUser(finalUserInfo);
      console.log('User state updated for normal flow:', finalUserInfo);
    }
  };

  const handleStartChat = async (fromAvatarSelection = false) => {
    console.log('=== HANDLE START CHAT CALLED ===');
    console.log('User exists:', !!user);
    console.log('User data:', user);
    console.log('Login context:', loginContext);
    console.log('From avatar selection:', fromAvatarSelection);
    console.log('===============================');
    
    trackEvent(ANALYTICS_EVENTS.START_CHAT_BUTTON_CLICK, {
      user_logged_in: !!user,
      page: 'landing',
      timestamp: Date.now()
    });
    
    // Check if user exists in state or localStorage
    let currentUser = user;
    if (!currentUser) {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try {
          currentUser = JSON.parse(savedUser);
          console.log('Restored user from localStorage:', currentUser);
        } catch (error) {
          console.warn('Failed to parse saved user:', error);
        }
      }
    }
    
    if (!currentUser) {
      // User is not logged in, implement guest login
      console.log('User not logged in, attempting guest login...');
      setIsMatchmaking(true);
      
      // Set login context to 'chat' so that after avatar selection, user goes to chat
      setLoginContext('chat');
      
      try {
        // Call guest login API using authAPI utility
        const guestData = await authAPI.guestLogin();
        console.log('Guest login successful:', guestData);
        
        // Create user info object in the same format as Google sign-in
        const userInfo = {
          ...guestData.user,
          backendToken: guestData.token,
          authProvider: 'guest',
          apiResponse: {
            user: guestData.user,
            token: guestData.token,
            isNewUser: true // Guest users are always new users
          }
        };
        
        // Store the backend token immediately
        localStorage.setItem('backend_jwt_token', guestData.token);
        localStorage.setItem('auth_token', guestData.token);
        
        // Track guest login
        trackEvent(ANALYTICS_EVENTS.GUEST_LOGIN_SUCCESS, {
          guest_id: guestData.user.id,
          timestamp: Date.now()
        });
        
        // Follow the same flow as Google sign-in - check if username setup is needed
        const hasCompletedUsernameSetup = localStorage.getItem('username_setup_completed');
        
        // Guest users should always go through username creation (they're new users)
        const shouldCreateUsername = true; // Always true for guest users
        
        if (shouldCreateUsername) {
          // Show username creation screen
          console.log('Showing username creation screen for guest user');
          setPendingUserInfo(userInfo);
          setIsUsernameCreationOpen(true);
        } else {
          // This shouldn't happen for guest users, but handle it just in case
          console.log('Completing guest login with existing username');
          setUser(userInfo);
          localStorage.setItem('user', JSON.stringify(userInfo));
        }
        
      } catch (error) {
        console.error('Error during guest login:', error);
        
        // Show specific error messages
        if (error.message.includes('Failed to fetch')) {
          alert('Could not connect to the server. Please check your internet connection.');
        } else if (error.message.includes('Guest login failed')) {
          alert('Guest login failed. Please try again.');
        } else {
          alert('Error during guest login. Please try again.');
        }
      } finally {
        setIsMatchmaking(false);
      }
      
      return;
    }
    
    // User is logged in, call matchmaking API
    console.log('User authenticated, calling matchmaking API...', currentUser);
    console.log('User ID:', currentUser.id);
    console.log('User username:', currentUser.username);
    console.log('User avatarId:', currentUser.avatarId);
    setIsMatchmaking(true);
    
    try {
      // Get the backend token
      const authToken = localStorage.getItem('backend_jwt_token');
      
      if (!authToken) {
        console.error('No auth token found');
        alert('Authentication token not found. Please log in again.');
        handleLogout();
        return;
      }
      
      // Call matchmaking API using utility
      const roomData = await roomAPI.matchmaking();
      
      console.log('=== MATCHMAKING RESPONSE ===');
      console.log('Room data:', roomData);
      console.log('===========================');
      
      // Store room data for the chat page
      localStorage.setItem('current_room', JSON.stringify(roomData));
      
      // If this is from avatar selection, call onStartChatting directly
      // Otherwise, proceed to chat with room data
      if (fromAvatarSelection) {
        console.log('Calling onStartChatting from avatar selection...');
        // Include user data in the room data for App.js
        const roomDataWithUser = {
          ...roomData,
          user: currentUser
        };
        onStartChatting(roomDataWithUser);
      } else {
        console.log('Calling onStartChatting from landing page...');
        onStartChatting(roomData);
      }
      
    } catch (error) {
      console.error('Error during matchmaking:', error);
      
      // Show specific error messages
      if (error.message.includes('Failed to fetch')) {
        alert('Could not connect to the matchmaking server. Please check if the backend is running on localhost:3000');
      } else if (error.message.includes('401')) {
        alert('Your session has expired. Please log in again.');
        handleLogout();
      } else if (error.message.includes('API call failed')) {
        alert(`Failed to find or create a room: ${error.message}`);
      } else {
        alert('Error starting chat. Please try again.');
      }
    } finally {
      setIsMatchmaking(false);
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('google_jwt_token');
    localStorage.removeItem('backend_jwt_token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('username_setup_completed');
    localStorage.removeItem('avatar_setup_completed');
    // Clear background-related localStorage
    localStorage.removeItem('selectedBackground');
    localStorage.removeItem('customBackgrounds');
    console.log('User logged out and all tokens and backgrounds cleared');
  };

  const handleChangeUsername = () => {
    setPendingUserInfo(user);
    setIsUsernameCreationOpen(true);
  };

  const handleChangeAvatar = () => {
    setPendingUserInfo(user);
    setIsAvatarSelectionOpen(true);
  };

  // Helper function to get avatar image path from avatarId
  const getAvatarImagePath = (avatarId) => {
    return avatarAssets.getAvatarIcon(avatarId);
  };

  const handleGifLoad = (gifName) => {
    setLoadedGifs(prev => new Set([...prev, gifName]));
  };

  // Debug: Log initial state
  useEffect(() => {
    console.log('FanimeLandingPage mounted');
  }, []);

  // Check for existing user session on component mount
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        // Ensure avatar URLs are full URLs when loading from storage
        const processedUser = avatarAssets.ensureUserAvatarUrls(user);
        setUser(processedUser);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('user');
      }
    }
  }, []);

  return (
    <div className="fanime-landing">
      {/* Background gradient */}
      <div className="landing-background"></div>
      
      {/* Only show main landing content when no modals are open */}
      {!isLoginModalOpen && !isUsernameCreationOpen && !isPermissionsScreenOpen && !isAvatarSelectionOpen && !isAboutModalOpen && !isTosModalOpen && !isPrivacyModalOpen && !isGuidelinesModalOpen && !isFaqModalOpen && (
        <>
          {/* Top bar */}
          <div className="top-bar">
            {/* Logo */}
            <div className="logo">
              <img src="/logo.svg" alt="Fanime Logo" className="logo-image" />
            </div>
            
            {/* User status */}
            {user && (
              <ProfileMenu 
                key={`${user.avatarId}-${Date.now()}`}
                userInfo={user}
                onEditProfile={handleChangeUsername}
                onChangeAvatar={handleChangeAvatar}
                onLogout={handleLogout}
              />
            )}
          </div>
          
          {/* Main content */}
          <div className="main-content">
            {/* Left side - Text content */}
            <div className="content-left">
              <h1 className="headline">
                Step into a World of Fun Conversations & Virtual Friendships.
              </h1>
              <p className="description">
                Join the next-gen social hub where creativity meets conversation.
              </p>
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button 
                  className={`cta-button ${!user ? 'login-required' : ''}`}
                  onClick={handleStartChat}
                  disabled={isMatchmaking}
                  style={!user ? {
                    background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
                    border: '2px solid #8b5cf6'
                  } : isMatchmaking ? {
                    opacity: 0.7,
                    cursor: 'not-allowed'
                  } : {}}
                >
                  {isMatchmaking ? 'Finding room...' : (user ? 'Start Chatting' : ' Start chatting')}
                </button>
              </div>
            </div>
            
            {/* Right side - Avatar showcase with reels */}
            <div className="avatar-showcase">
              {/* Reels container */}
              <div className="reels-container">
                {/* First set of reels */}
                <div className="reel-item">
                  {!loadedGifs.has('reel_1') && (
                    <div className="gif-spinner">
                      <div className="spinner"></div>
                    </div>
                  )}
                  <img 
                    src="/images/reel_1.gif" 
                    alt="Reel 1" 
                    className={`reel-gif ${loadedGifs.has('reel_1') ? 'loaded' : 'loading'}`}
                    onLoad={() => handleGifLoad('reel_1')}
                  />
                </div>
                
                <div className="reel-item">
                  {!loadedGifs.has('reel_2') && (
                    <div className="gif-spinner">
                      <div className="spinner"></div>
                    </div>
                  )}
                  <img 
                    src="/images/reel_2.gif" 
                    alt="Reel 2" 
                    className={`reel-gif ${loadedGifs.has('reel_2') ? 'loaded' : 'loading'}`}
                    onLoad={() => handleGifLoad('reel_2')}
                  />
                </div>
                
                <div className="reel-item">
                  {!loadedGifs.has('reel_3') && (
                    <div className="gif-spinner">
                      <div className="spinner"></div>
                    </div>
                  )}
                  <img 
                    src="/images/reel_3.gif" 
                    alt="Reel 3" 
                    className={`reel-gif ${loadedGifs.has('reel_3') ? 'loaded' : 'loading'}`}
                    onLoad={() => handleGifLoad('reel_3')}
                  />
                </div>
                
                {/* Duplicate set for seamless loop */}
                <div className="reel-item">
                  {!loadedGifs.has('reel_1') && (
                    <div className="gif-spinner">
                      <div className="spinner"></div>
                    </div>
                  )}
                  <img 
                    src="/images/reel_1.gif" 
                    alt="Reel 1" 
                    className={`reel-gif ${loadedGifs.has('reel_1') ? 'loaded' : 'loading'}`}
                    onLoad={() => handleGifLoad('reel_1')}
                  />
                </div>
                
                <div className="reel-item">
                  {!loadedGifs.has('reel_2') && (
                    <div className="gif-spinner">
                      <div className="spinner"></div>
                    </div>
                  )}
                  <img 
                    src="/images/reel_2.gif" 
                    alt="Reel 2" 
                    className={`reel-gif ${loadedGifs.has('reel_2') ? 'loaded' : 'loading'}`}
                    onLoad={() => handleGifLoad('reel_2')}
                  />
                </div>
                
                <div className="reel-item">
                  {!loadedGifs.has('reel_3') && (
                    <div className="gif-spinner">
                      <div className="spinner"></div>
                    </div>
                  )}
                  <img 
                    src="/images/reel_3.gif" 
                    alt="Reel 3" 
                    className={`reel-gif ${loadedGifs.has('reel_3') ? 'loaded' : 'loading'}`}
                    onLoad={() => handleGifLoad('reel_3')}
                  />
                </div>
              </div>
            </div>
        </div>

        {/* Footer */}
        <footer className="landing-footer">
          <nav>
            <a href="#" onClick={(e) => {
              e.preventDefault();
              trackEvent(ANALYTICS_EVENTS.ABOUT_MODAL_OPEN, {
                source: 'footer',
                timestamp: Date.now()
              });
              setIsAboutModalOpen(true);
            }}>About Us</a>
            <a href="#" onClick={(e) => {
              e.preventDefault();
              trackEvent(ANALYTICS_EVENTS.TOS_MODAL_OPEN, {
                source: 'footer',
                timestamp: Date.now()
              });
              setIsTosModalOpen(true);
            }}>Terms of Service</a>
            <a href="#" onClick={(e) => {
              e.preventDefault();
              trackEvent(ANALYTICS_EVENTS.PRIVACY_MODAL_OPEN, {
                source: 'footer',
                timestamp: Date.now()
              });
              setIsPrivacyModalOpen(true);
            }}>Privacy Policy</a>
            <a href="#" onClick={(e) => {
              e.preventDefault();
              trackEvent(ANALYTICS_EVENTS.GUIDELINES_MODAL_OPEN, {
                source: 'footer',
                timestamp: Date.now()
              });
              setIsGuidelinesModalOpen(true);
            }}>Community Guidelines</a>
            <a href="#" onClick={(e) => {
              e.preventDefault();
              trackEvent(ANALYTICS_EVENTS.FAQ_MODAL_OPEN, {
                source: 'footer',
                timestamp: Date.now()
              });
              setIsFaqModalOpen(true);
            }}>FAQ</a>
          </nav>
          {/* <div className="footer-icons">
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" aria-label="Discord">
              <i className="icon-discord" />
            </a>
            <a href="#" aria-label="Close">
              <i className="icon-close" />
            </a>
          </div> */}
        </footer>
        </>
      )}
        
        {/* Mobile Warning Popup */}
        {showMobileWarning && (
          <div className="mobile-warning-overlay">
            <div className="mobile-warning-popup">
              <div className="mobile-warning-icon">
                <i className="icon-desktop"></i>
              </div>
              <h2 className="mobile-warning-title">Desktop Experience Required</h2>
              <p className="mobile-warning-message">
                <span className="brand-accent">wybli</span> works best on desktop or laptop browsers for the optimal experience. 
                Mobile devices may have limited functionality and performance issues.
              </p>
              <div className="mobile-warning-features">
                <div className="feature-item">
                  <i className="icon-camera"></i>
                  <span>Better camera performance</span>
                </div>
                <div className="feature-item">
                  <i className="icon-avatar"></i>
                  <span>Full avatar customization</span>
                </div>
                <div className="feature-item">
                  <i className="icon-chat"></i>
                  <span>Enhanced chat experience</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Safari Browser Warning Popup */}
        {showSafariWarning && (
          <div className="safari-warning-overlay">
            <div className="safari-warning-popup">
              <div className="safari-warning-icon">
                <i className="icon-browser"></i>
              </div>
              <h2 className="safari-warning-title">Browser Recommendation</h2>
              <p className="safari-warning-message">
                <span className="brand-accent">wybli</span> works best on Chrome and Firefox browsers for optimal performance and features. 
                Safari may have limited functionality and compatibility issues.
              </p>
              <div className="safari-warning-features">
                <div className="feature-item">
                  <i className="icon-performance"></i>
                  <span>Better performance</span>
                </div>
                <div className="feature-item">
                  <i className="icon-compatibility"></i>
                  <span>Full feature compatibility</span>
                </div>
                <div className="feature-item">
                  <i className="icon-updates"></i>
                  <span>Latest web standards</span>
                </div>
              </div>
              <div className="safari-warning-actions">
                <button 
                  className="safari-warning-continue"
                  onClick={() => setShowSafariWarning(false)}
                >
                  Continue with Safari
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Login Modal */}
        <LoginModal 
          isOpen={isLoginModalOpen}
          onClose={() => {
            setIsLoginModalOpen(false);
            setLoginContext('general');
          }}
          onLogin={(userInfo) => {
            handleLogin(userInfo);
            setIsLoginModalOpen(false);
          }}
          context={loginContext}
          onOpenTerms={() => {
            setIsLoginModalOpen(false);
            setIsTosModalOpen(true);
          }}
          onOpenPrivacy={() => {
            setIsLoginModalOpen(false);
            setIsPrivacyModalOpen(true);
          }}
        />
        
        {/* Username Creation Modal */}
        <UsernameCreation 
          isOpen={isUsernameCreationOpen}
          onClose={() => {
            setIsUsernameCreationOpen(false);
            setPendingUserInfo(null);
          }}
          onComplete={handleUsernameCreationComplete}
          userInfo={pendingUserInfo}
        />
        
        {/* Permissions Screen */}
        <PermissionsScreen 
          isOpen={isPermissionsScreenOpen}
          onClose={() => {
            setIsPermissionsScreenOpen(false);
            setPendingUserInfo(null);
          }}
          onComplete={handlePermissionsComplete}
          userInfo={pendingUserInfo}
        />
        
        {/* Avatar Selection Modal */}
        <AvatarSelection 
          isOpen={isAvatarSelectionOpen}
          onClose={() => {
            setIsAvatarSelectionOpen(false);
            setPendingUserInfo(null);
          }}
          onComplete={handleAvatarSelectionComplete}
          userInfo={pendingUserInfo}
        />
        
        {/* Info Modals */}
        <InfoModal
          isOpen={isAboutModalOpen}
          onClose={() => setIsAboutModalOpen(false)}
          title="About Us"
          content={modalContent.about}
          modalType="about"
        />
        
        <InfoModal
          isOpen={isTosModalOpen}
          onClose={() => setIsTosModalOpen(false)}
          title="Terms of Service"
          content={modalContent.tos}
          modalType="terms"
        />
        
        <InfoModal
          isOpen={isPrivacyModalOpen}
          onClose={() => setIsPrivacyModalOpen(false)}
          title="Privacy Policy"
          content={modalContent.privacy}
          modalType="privacy"
        />
        
        <InfoModal
          isOpen={isGuidelinesModalOpen}
          onClose={() => setIsGuidelinesModalOpen(false)}
          title="Community Guidelines"
          content={modalContent.guidelines}
          modalType="guidelines"
        />
        
        <InfoModal
          isOpen={isFaqModalOpen}
          onClose={() => setIsFaqModalOpen(false)}
          title="FAQ"
          content={modalContent.faq}
          modalType="faq"
        />
      </div>
    );
  };

export default FanimeLandingPage; 